import React, {createContext, useCallback, useContext, useEffect} from 'react'

import {useEvents} from '.'
import algoliasearchHelper from 'algoliasearch-helper'
import flatten from 'lodash/flatten'
import isArray from 'lodash/isArray'
import keys from 'lodash/keys'
import mapKeys from 'lodash/mapKeys'
import mapValues from 'lodash/mapValues'
import pickBy from 'lodash/pickBy'
import toPairs from 'lodash/toPairs'
import {useRouter} from 'next/router'
import qs from 'query-string'

import {
  ActiveFilterProps,
  compileActiveFilters,
  compileFacets,
  CompiledFacets,
  FacetOrderValues,
} from '../api/algolia'
import {VariantList} from '../api/rest'
import {CommonContextProps} from './common'

interface SearchContextProps {
  searchResults: algoliasearchHelper.SearchResults<VariantList>
  facets: CompiledFacets
  activeFilters: ActiveFilterProps[]
  queryParams: Record<string, (string | number)[]>
  page: number
  pageSize: number
  updateFilters: (filterUpdate: FilterUpdate, clear?: boolean) => void
  removeFilter: (filter: ActiveFilterProps) => void
  clearFilters: () => void
  facetNames: Record<string, string>
  setSorting: (sort: string) => void
  setPageSize: (pageSize: number) => void
  facetOrder: string[]
  facetOrderValues: FacetOrderValues
  promoContent: string
}

export const SearchContext = createContext<Partial<SearchContextProps>>({})

export interface SearchProviderProps {
  results: algoliasearchHelper.SearchResults<VariantList>
  common: Partial<CommonContextProps>
  attrNameMap: Record<string, string>
  children: React.ReactNode
}

export interface FilterUpdate {
  [param: string]:
    | false
    | {
        [value: string]: boolean | number | string
      }
}

export function SearchProvider({
  results,
  common,
  attrNameMap,
  children,
}: SearchProviderProps) {
  const {setCurrentQueryID, setCurrentIndex} = useEvents()
  const router = useRouter()
  const queryParams = mapValues(
    qs.parse(router.asPath.split('?')[1] ?? ''),
    (v) => (isArray(v) ? v : v.split(',')),
  )
  const activeCategorySlug = router.query.slug as string | undefined
  const facets = compileFacets(results, common, queryParams, activeCategorySlug)
  const inCategoryRoute = router.pathname.indexOf('/voruflokkar') === 0

  const facetOrder = results?.renderingContent?.facetOrdering?.facets?.order
  const facetOrderValues = results?.renderingContent?.facetOrdering?.values

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const promoContent = results?.userData?.find((ud: any) =>
    keys(ud).find((p) => p === 'promo_content'),
  )?.promo_content

  const queryID = results?.queryID
  const index = results?.index

  if (facets.categories?.options.length < 2) {
    delete facets.categories
  }

  const activeFilters = compileActiveFilters(facets)

  const page = parseInt(queryParams.page?.[0] || '1', 10)
  const pageSize = parseInt(queryParams.pageSize?.[0] || '24', 10)

  const facetKeys = flatten(
    toPairs(facets).map(([k, facet]) =>
      facet.type === 'range' ? [facet.minAttr, facet.maxAttr] : k,
    ),
  )

  const facetNames = {
    categories: 'Flokkar',
    priceRange: 'Verð',
    ...mapKeys(attrNameMap ?? {}, (_, k) => `attributes.${k}`),
  }

  const commitFilterQueryParams = useCallback(
    (filterParams: Record<string, string[]>) => {
      const nextQueryParams = {
        ...filterParams,
        ...pickBy(queryParams, (_, k) => !facetKeys.includes(k)),
      }
      delete nextQueryParams.page

      const query = mapValues(nextQueryParams, (v) => v.join(','))

      if (inCategoryRoute) {
        query.slug = activeCategorySlug
      }
      router.replace({query}, undefined, {
        shallow: true,
      })
    },
    [activeCategorySlug, facetKeys, queryParams, inCategoryRoute, router],
  )

  const updateFilters = useCallback(
    (filterUpdate: FilterUpdate, clear = false) => {
      let filterParams = {}

      if (clear) {
        filterParams = {}
      } else {
        const facetPairs = toPairs(facets)

        facetPairs.forEach(([param, facet]) => {
          if (facet.type === 'choice' || facet.type === 'hierarchical') {
            const selectedOptions = facet.options
              .filter(
                (opt) =>
                  filterUpdate[param] !== false &&
                  (filterUpdate[param]?.[opt.value] ?? opt.active),
              )
              .map((opt) => `${opt.value}`)

            if (selectedOptions.length) {
              filterParams[param] = selectedOptions
            }
          }

          if (facet.type === 'range') {
            let minValue = filterUpdate?.[param]?.[facet.minAttr]
            if (!minValue && minValue !== false) {
              minValue = facet.minValue
            }
            if (minValue) {
              filterParams[facet.minAttr] = [`${Number(minValue)}`]
            }

            let maxValue = filterUpdate?.[param]?.[facet.maxAttr]
            if (!maxValue && maxValue !== false) {
              maxValue = facet.maxValue
            }
            if (maxValue) {
              filterParams[facet.maxAttr] = [`${Number(maxValue)}`]
            }
          }
        })
      }

      commitFilterQueryParams(filterParams)
    },
    [facets, commitFilterQueryParams],
  )

  const updateQueryParams = useCallback(
    (params) => {
      if (inCategoryRoute) {
        params.slug = activeCategorySlug
      }

      const search = qs.stringify(params, {arrayFormat: 'comma'})

      router.replace({search}, undefined, {
        shallow: true,
      })
    },
    [router, activeCategorySlug, inCategoryRoute],
  )

  const setSorting = useCallback(
    (sort: string) => {
      updateQueryParams({...queryParams, sort})
    },
    [updateQueryParams, queryParams],
  )

  const setPageSize = useCallback(
    (pageSize: number) => {
      delete queryParams.page
      updateQueryParams({...queryParams, pageSize})
    },
    [updateQueryParams, queryParams],
  )

  const removeFilter = useCallback(
    (filter: ActiveFilterProps) => {
      if (filter.attr === 'priceRange') {
        updateFilters({
          [filter.attr]: {
            [filter.min]: false,
            [filter.max]: false,
          },
        })
      } else {
        updateFilters({[filter.attr]: {[filter.value]: false}})
      }
    },
    [updateFilters],
  )

  const clearFilters = useCallback(() => {
    updateFilters({}, true)
  }, [updateFilters])

  useEffect(() => {
    queryID && setCurrentQueryID(queryID)
  }, [queryID, setCurrentQueryID])

  useEffect(() => {
    index && setCurrentIndex(index)
  }, [index, setCurrentIndex])

  return (
    <SearchContext.Provider
      value={{
        searchResults: results,
        facets,
        activeFilters,
        queryParams,
        page,
        pageSize,
        updateFilters,
        removeFilter,
        clearFilters,
        facetNames,
        setSorting,
        setPageSize,
        facetOrder,
        facetOrderValues,
        promoContent,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export function useSearch() {
  return useContext(SearchContext) as SearchContextProps
}
